"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Control, FieldErrors, useFormContext } from "react-hook-form";

import { FinanceFormData, Step1Data } from "../schema";
import FormRadioGroup from "@/components/common/forms/FormRadioGroup";
import FormTextField from "@/components/common/forms/FormTextField";
import Button from "@/components/ui/buttons/Button";
import { readUTMCookies } from "@/lib/utm-tracker";

interface QuestionStep1Props {
  control: Control<FinanceFormData>;
  errors: FieldErrors<FinanceFormData>;
  NextStep: () => void;
  PrevStep: () => void;
  stepIndex: number;
}

const QuestionStep1: React.FC<QuestionStep1Props> = ({ control, NextStep }) => {
  const t = useTranslations("Finance.form.step1");
  const tButtons = useTranslations("Finance.form.buttons");

  const { watch, trigger, setError, formState, setValue } = useFormContext<Step1Data>();
  const existingLoans = watch("existingLoans");
  const hasCreditScore = watch("hasCreditScore");

  const marketingDataWatch = watch("marketing");
  const marketingData = readUTMCookies();

  if (!!marketingData && !marketingDataWatch) {
    setValue("marketing", marketingData);
  }

  const handleNext = async () => {
    const isValid = await trigger(["existingLoans", "totalMonthlyInstallments", "hasCreditScore"]);

    if (existingLoans === "yes") {
      const totalMonthlyInstallments = watch("totalMonthlyInstallments");
      if (!totalMonthlyInstallments || totalMonthlyInstallments === 0) {
        setError("totalMonthlyInstallments", {
          message: t("question1.error"),
        });
        return;
      }
    } else {
      setValue("totalMonthlyInstallments", 0);
    }

    if (hasCreditScore === "yes") {
      const totalCreditLimit = watch("totalCreditLimit");
      if (!totalCreditLimit || totalCreditLimit === 0) {
        setError("totalCreditLimit", {
          message: t("question2.error"),
        });
        return;
      }
    } else {
      setValue("totalCreditLimit", 0);
    }

    if (isValid) {
      NextStep();
    }
  };

  const loanOptions = [
    { value: "yes", label: t("question1.options.yes") },
    { value: "no", label: t("question1.options.no"), isNegative: true },
  ];

  const creditScoreOptions = [
    { value: "yes", label: t("question2.options.yes") },
    { value: "no", label: t("question2.options.no"), isNegative: true },
  ];

  const disputeOptions = [
    { value: "yes", label: t("question3.options.yes"), isNegative: true },
    { value: "no", label: t("question3.options.no") },
  ];

  return (
    <div className="mt-10">
      <div className="space-y-8">
        <div className="">
          <h4 className="inter mb-4 text-base font-semibold text-gray-900">{t("question1.title")}</h4>
          <FormRadioGroup<FinanceFormData> name="existingLoans" control={control} label="" options={loanOptions} />

          <AnimatePresence>
            {existingLoans === "yes" && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mt-6">
                  <FormTextField<FinanceFormData>
                    control={control}
                    name="totalMonthlyInstallments"
                    placeholder={t("question1.placeholder")}
                    type="number"
                    required={existingLoans === "yes"}
                    formatNumber
                    label={t("question1.label")}
                    className="inter h-[60px]"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div>
          <h4 className="inter mb-4 text-base font-semibold text-gray-900">{t("question2.title")}</h4>
          <FormRadioGroup<FinanceFormData>
            name="hasCreditScore"
            control={control}
            label=""
            options={creditScoreOptions}
          />

          <AnimatePresence>
            {hasCreditScore === "yes" && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mt-6">
                  <FormTextField<FinanceFormData>
                    control={control}
                    name="totalCreditLimit"
                    placeholder={t("question2.placeholder")}
                    type="number"
                    required={hasCreditScore === "yes"}
                    formatNumber
                    label={t("question2.label")}
                    className="h-[60px]"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div>
          <h4 className="inter mb-4 text-base font-semibold text-gray-900">{t("question3.title")}</h4>
          <FormRadioGroup<FinanceFormData>
            name="previousDisputes"
            control={control}
            label=""
            options={disputeOptions}
          />
        </div>
      </div>

      {/* Use a custom "Next" button in this step for custom validation logic in handleNext() */}
      <div className="mt-4 flex justify-end">
        <Button
          buttonText={tButtons("next")}
          onClick={handleNext}
          bgColor="primary"
          disabled={!formState.isValid}
          textColor="white"
          className="w-full px-9 py-3 text-center transition-all duration-300 ease-in-out hover:bg-primary-dark disabled:opacity-50 md:max-w-fit"
        />
      </div>
    </div>
  );
};

export default QuestionStep1;
